import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  PwmProfileStrategyGetByIdDocument,
  PwmProfileStrategyListPaginatedDocument,
  PwmProfileStrategyCreateDocument,
  PwmProfileStrategyUpdateDocument,
  PwmProfileStrategyDeleteDocument,
} from "api/generated";
import { Optional } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IPwmProfileStrategyCreateVars,
  IPwmProfileStrategyModel,
  IPwmProfileStrategyPaginatedResponse,
  IPwmProfileStrategyPaginatedVars,
  IPwmProfileStrategyUpdateVars,
} from "./types";

export async function getPwmProfileStrategy(
  id: number
): Promise<Optional<IPwmProfileStrategyModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileStrategyGetByIdDocument,
      variables: {
        id,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileStrategyGetById;
  } catch (error) {
    throw error;
  }
}

export async function getPwmProfileStrategyPaginated(
  variables: IPwmProfileStrategyPaginatedVars
): Promise<Optional<IPwmProfileStrategyPaginatedResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: PwmProfileStrategyListPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.pwmProfileStrategyListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createPwmProfileStrategy(
  variables: IPwmProfileStrategyCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileStrategyCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict own lists + parent PWM profile entity
    executeEviction(client, {
      typename: "PwmProfileStrategy",
      fieldNames: ["pwmProfileStrategyListPaginated"],
    });

    executeEviction(client, {
      typename: "PwmProfile",
      id: variables.profileId.toString(),
      fieldNames: ["pwmProfileGetById"],
    });

    return Boolean(res?.data?.pwmProfileStrategyCreate);
  } catch (error) {
    throw error;
  }
}

export async function updatePwmProfileStrategy(
  variables: IPwmProfileStrategyUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileStrategyUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict updated entity + own lists + getById
    executeEviction(client, {
      typename: "PwmProfileStrategy",
      id: variables.id.toString(),
      fieldNames: [
        "pwmProfileStrategyListPaginated",
        "pwmProfileStrategyGetById",
      ],
    });

    // Evict all parent PWM profile entities (profileId not available)
    executeEviction(client, {
      typename: "PwmProfile",
      fieldNames: ["pwmProfileGetById"],
    });

    return Boolean(res?.data?.pwmProfileStrategyUpdate);
  } catch (error) {
    throw error;
  }
}

export async function deletePwmProfileStrategy(id: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: PwmProfileStrategyDeleteDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    // Evict deleted entity + own lists
    executeEviction(client, {
      typename: "PwmProfileStrategy",
      id: id.toString(),
      fieldNames: ["pwmProfileStrategyListPaginated"],
    });

    // Evict all parent PWM profile entities (profileId not available)
    executeEviction(client, {
      typename: "PwmProfile",
      fieldNames: ["pwmProfileGetById"],
    });

    return Boolean(res?.data?.pwmProfileStrategyDelete);
  } catch (error) {
    throw error;
  }
}
